<template>
  <div class="theme-color-picker">
    <!-- Theme Color Editor -->
    <section class="theme-color-picker__widget">
      <header class="theme-color-picker__widget-header">
        <v-icon size="18" class="theme-color-picker__widget-icon">mdi-palette</v-icon>
        <h2 class="theme-color-picker__widget-title">Color Editor</h2>
      </header>

      <div class="theme-color-picker__content">
        <!-- Color Categories -->
        <div class="theme-color-picker__categories">
          <!-- Primary Colors -->
          <div class="theme-color-picker__category">
            <h3 class="theme-color-picker__category-title">Primary</h3>
            <div class="theme-color-picker__colors">
              <ColorPickerItem
                v-for="color in primaryColors"
                :key="color.vuetifyKey"
                :label="color.label"
                :color-key="color.vuetifyKey"
                :value="getCurrentColor(color.vuetifyKey)"
                @update="updateColor"
              />
            </div>
          </div>

          <!-- Secondary Colors -->
          <div class="theme-color-picker__category">
            <h3 class="theme-color-picker__category-title">Secondary</h3>
            <div class="theme-color-picker__colors">
              <ColorPickerItem
                v-for="color in secondaryColors"
                :key="color.vuetifyKey"
                :label="color.label"
                :color-key="color.vuetifyKey"
                :value="getCurrentColor(color.vuetifyKey)"
                @update="updateColor"
              />
            </div>
          </div>

          <!-- Tertiary Colors -->
          <div class="theme-color-picker__category">
            <h3 class="theme-color-picker__category-title">Tertiary</h3>
            <div class="theme-color-picker__colors">
              <ColorPickerItem
                v-for="color in tertiaryColors"
                :key="color.vuetifyKey"
                :label="color.label"
                :color-key="color.vuetifyKey"
                :value="getCurrentColor(color.vuetifyKey)"
                @update="updateColor"
              />
            </div>
          </div>

          <!-- Background Colors -->
          <div class="theme-color-picker__category">
            <h3 class="theme-color-picker__category-title">Background</h3>
            <div class="theme-color-picker__colors">
              <ColorPickerItem
                v-for="color in backgroundColors"
                :key="color.vuetifyKey"
                :label="color.label"
                :color-key="color.vuetifyKey"
                :value="getCurrentColor(color.vuetifyKey)"
                @update="updateColor"
              />
            </div>
          </div>

          <!-- Surface Colors -->
          <div class="theme-color-picker__category">
            <h3 class="theme-color-picker__category-title">Surface</h3>
            <div class="theme-color-picker__colors">
              <ColorPickerItem
                v-for="color in surfaceColors"
                :key="color.vuetifyKey"
                :label="color.label"
                :color-key="color.vuetifyKey"
                :value="getCurrentColor(color.vuetifyKey)"
                @update="updateColor"
              />
            </div>
          </div>

          <!-- Status Colors -->
          <div class="theme-color-picker__category">
            <h3 class="theme-color-picker__category-title">Status</h3>
            <div class="theme-color-picker__colors">
              <ColorPickerItem
                v-for="color in statusColors"
                :key="color.vuetifyKey"
                :label="color.label"
                :color-key="color.vuetifyKey"
                :value="getCurrentColor(color.vuetifyKey)"
                @update="updateColor"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Export Section -->
    <section class="theme-color-picker__widget">
      <header class="theme-color-picker__widget-header">
        <v-icon size="18" class="theme-color-picker__widget-icon">mdi-export</v-icon>
        <h2 class="theme-color-picker__widget-title">Export</h2>
      </header>

      <div class="theme-color-picker__export">
        <v-btn
          color="primary"
          variant="tonal"
          size="small"
          block
          @click="copyToClipboard"
        >
          <v-icon start size="16">mdi-content-copy</v-icon>
          Copy Key/Values
        </v-btn>

        <v-btn
          color="secondary"
          variant="outlined"
          size="small"
          block
          class="mt-2"
          @click="showExportDialog = true"
        >
          <v-icon start size="16">mdi-eye</v-icon>
          View Export
        </v-btn>

        <v-btn
          variant="text"
          size="small"
          block
          class="mt-2"
          @click="resetToDefaults"
        >
          <v-icon start size="16">mdi-restore</v-icon>
          Reset to Defaults
        </v-btn>
      </div>
    </section>

    <!-- Export Dialog -->
    <v-dialog v-model="showExportDialog" max-width="600">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon start>mdi-code-json</v-icon>
          Theme Export
          <v-spacer />
          <v-btn icon variant="text" @click="showExportDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-textarea
            :model-value="exportText"
            readonly
            rows="15"
            variant="outlined"
            hide-details
            class="export-textarea"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" @click="copyToClipboard">
            <v-icon start>mdi-content-copy</v-icon>
            Copy to Clipboard
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar for copy confirmation -->
    <v-snackbar v-model="showCopySnackbar" :timeout="2000" color="success">
      Copied to clipboard!
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted } from "vue";
import { useTheme } from "vuetify";
import ColorPickerItem from "./ColorPickerItem.vue";

const theme = useTheme();
const showExportDialog = ref(false);
const showCopySnackbar = ref(false);

// Force re-render key for color reactivity
const colorUpdateKey = ref(0);

// Cached color values from CSS - updates when colorUpdateKey changes
const currentColors = ref<Record<string, string>>({});

// Debounce timer for theme refresh
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Convert hex color to RGB triplet string for Vuetify CSS variables
 */
function hexToRgbTriplet(hex: string): string {
  // Remove # if present
  const cleanHex = hex.replace("#", "");

  // Parse the hex values
  let r: number, g: number, b: number;

  if (cleanHex.length === 3) {
    r = parseInt(cleanHex[0] + cleanHex[0], 16);
    g = parseInt(cleanHex[1] + cleanHex[1], 16);
    b = parseInt(cleanHex[2] + cleanHex[2], 16);
  } else {
    r = parseInt(cleanHex.substring(0, 2), 16);
    g = parseInt(cleanHex.substring(2, 4), 16);
    b = parseInt(cleanHex.substring(4, 6), 16);
  }

  return `${r},${g},${b}`;
}

// Store all color overrides for the current theme
const colorOverrides = ref<Record<string, string>>({});

/**
 * Generate and apply the override stylesheet
 * This creates a separate stylesheet with direct RGB values that override Vuetify's CSS variable-based colors
 */
function applyColorOverrides() {
  // Get or create the override stylesheet
  let overrideStyle = document.getElementById("theme-color-picker-overrides") as HTMLStyleElement | null;
  if (!overrideStyle) {
    overrideStyle = document.createElement("style");
    overrideStyle.id = "theme-color-picker-overrides";
    document.head.appendChild(overrideStyle);
  }

  // Build CSS for all color overrides
  const cssRules: string[] = [];

  // Build CSS variable overrides string
  const cssVarOverrides: string[] = [];
  for (const [vuetifyKey, hexColor] of Object.entries(colorOverrides.value)) {
    const rgbTriplet = hexToRgbTriplet(hexColor);
    cssVarOverrides.push(`--v-theme-${vuetifyKey}: ${rgbTriplet}`);
  }

  // Apply to BOTH dark and light themes so the preview always updates
  if (cssVarOverrides.length > 0) {
    const cssVarString = cssVarOverrides.join("; ");
    cssRules.push(`.v-theme--dark { ${cssVarString} !important; }`);
    cssRules.push(`.v-theme--light { ${cssVarString} !important; }`);
  }

  // Also add direct color classes for Vuetify utility classes (these work regardless of theme)
  for (const [vuetifyKey, hexColor] of Object.entries(colorOverrides.value)) {
    const rgbTriplet = hexToRgbTriplet(hexColor);

    // Background color classes
    cssRules.push(`.bg-${vuetifyKey} { background-color: rgb(${rgbTriplet}) !important; }`);

    // Text color classes
    cssRules.push(`.text-${vuetifyKey} { color: rgb(${rgbTriplet}) !important; }`);

    // Border color classes
    cssRules.push(`.border-${vuetifyKey} { border-color: rgb(${rgbTriplet}) !important; }`);
  }

  overrideStyle.textContent = cssRules.join("\n");

  // Also directly update CSS variables on ALL theme elements for immediate effect
  const themeElements = document.querySelectorAll(".v-theme--dark, .v-theme--light");
  themeElements.forEach((themeElement) => {
    for (const [vuetifyKey, hexColor] of Object.entries(colorOverrides.value)) {
      const rgbTriplet = hexToRgbTriplet(hexColor);
      (themeElement as HTMLElement).style.setProperty(`--v-theme-${vuetifyKey}`, rgbTriplet);
    }
  });
}

/**
 * Force style recalculation on elements using the specified color
 * This clones and replaces elements to force the browser to recompute their styles
 */
function forceStyleRecalc(vuetifyKey: string) {
  // Find all elements that use this color
  const selectors = [
    `.bg-${vuetifyKey}`,
    `.text-${vuetifyKey}`,
    `.border-${vuetifyKey}`,
  ];

  const elements = document.querySelectorAll(selectors.join(", "));

  // Clone and replace each element to force style recalculation
  elements.forEach((el) => {
    const parent = el.parentNode;
    if (parent) {
      const clone = el.cloneNode(true);
      parent.replaceChild(clone, el);
    }
  });
}

// Color configurations mapping to Vuetify keys
interface ColorConfig {
  vuetifyKey: string;
  label: string;
  exportKeyDark: string;
  exportKeyLight: string;
}

const primaryColors: ColorConfig[] = [
  { vuetifyKey: "primary", label: "Primary", exportKeyDark: "ui-primary-color-dark", exportKeyLight: "ui-primary-color-light" },
  { vuetifyKey: "primary-accent", label: "Accent", exportKeyDark: "ui-primary-accent-dark", exportKeyLight: "ui-primary-accent-light" },
  { vuetifyKey: "on-primary", label: "On Primary", exportKeyDark: "ui-on-primary-dark", exportKeyLight: "ui-on-primary-light" },
];

const secondaryColors: ColorConfig[] = [
  { vuetifyKey: "secondary", label: "Secondary", exportKeyDark: "ui-secondary-color-dark", exportKeyLight: "ui-secondary-color-light" },
  { vuetifyKey: "secondary-accent", label: "Accent", exportKeyDark: "ui-secondary-accent-dark", exportKeyLight: "ui-secondary-accent-light" },
  { vuetifyKey: "on-secondary", label: "On Secondary", exportKeyDark: "ui-on-secondary-dark", exportKeyLight: "ui-on-secondary-light" },
];

const tertiaryColors: ColorConfig[] = [
  { vuetifyKey: "tertiary", label: "Tertiary", exportKeyDark: "ui-tertiary-color-dark", exportKeyLight: "ui-tertiary-color-light" },
  { vuetifyKey: "tertiary-accent", label: "Accent", exportKeyDark: "ui-tertiary-accent-dark", exportKeyLight: "ui-tertiary-accent-light" },
  { vuetifyKey: "on-tertiary", label: "On Tertiary", exportKeyDark: "ui-on-tertiary-dark", exportKeyLight: "ui-on-tertiary-light" },
];

const backgroundColors: ColorConfig[] = [
  { vuetifyKey: "background", label: "Background", exportKeyDark: "ui-background-color-dark", exportKeyLight: "ui-background-color-light" },
  { vuetifyKey: "surface", label: "Surface", exportKeyDark: "ui-background-secondary-dark", exportKeyLight: "ui-background-secondary-light" },
  { vuetifyKey: "on-background", label: "On Background", exportKeyDark: "ui-on-background-dark", exportKeyLight: "ui-on-background-light" },
];

const surfaceColors: ColorConfig[] = [
  { vuetifyKey: "surface-variant", label: "Surface Variant", exportKeyDark: "ui-surface-variant-dark", exportKeyLight: "ui-surface-variant-light" },
  { vuetifyKey: "on-surface", label: "On Surface", exportKeyDark: "ui-on-surface-dark", exportKeyLight: "ui-on-surface-light" },
  { vuetifyKey: "inverse-surface", label: "Inverse Surface", exportKeyDark: "ui-inverse-surface-dark", exportKeyLight: "ui-inverse-surface-light" },
  { vuetifyKey: "outline", label: "Outline", exportKeyDark: "ui-outline-dark", exportKeyLight: "ui-outline-light" },
  { vuetifyKey: "outline-variant", label: "Outline Variant", exportKeyDark: "ui-outline-variant-dark", exportKeyLight: "ui-outline-variant-light" },
];

const statusColors: ColorConfig[] = [
  { vuetifyKey: "error", label: "Error", exportKeyDark: "ui-error-color-dark", exportKeyLight: "ui-error-color-light" },
  { vuetifyKey: "warning", label: "Warning", exportKeyDark: "ui-warning-color-dark", exportKeyLight: "ui-warning-color-light" },
  { vuetifyKey: "info", label: "Info", exportKeyDark: "ui-info-color-dark", exportKeyLight: "ui-info-color-light" },
  { vuetifyKey: "success", label: "Success", exportKeyDark: "ui-success-color-dark", exportKeyLight: "ui-success-color-light" },
];

// All color configs for export
const allColorConfigs = [
  ...primaryColors,
  ...secondaryColors,
  ...tertiaryColors,
  ...backgroundColors,
  ...surfaceColors,
  ...statusColors,
];

// Read a single color from actual computed CSS variables
function readColorFromCSS(vuetifyKey: string): string {
  // Find the theme element in the DOM - use whichever theme element actually exists
  // (prefer dark if both exist since that's the default)
  const themeElement = (document.querySelector('.v-theme--dark') ||
                        document.querySelector('.v-theme--light')) as HTMLElement;

  if (themeElement) {
    // Read the actual computed CSS variable value
    const cssVarValue = getComputedStyle(themeElement).getPropertyValue(`--v-theme-${vuetifyKey}`).trim();

    if (cssVarValue) {
      // CSS variable values are RGB triplets like "123, 45, 67" or "123,45,67"
      const parts = cssVarValue.split(/[,\s]+/).map(Number).filter(n => !isNaN(n));
      if (parts.length >= 3) {
        return rgbToHex(parts[0], parts[1], parts[2]);
      }
    }
  }

  // Fallback to Vuetify theme object if CSS variable not found
  const currentThemeName = theme.global.name.value;
  const currentTheme = theme.themes.value[currentThemeName];
  const color = currentTheme.colors[vuetifyKey];

  if (!color) return "#000000";

  // If it's already a hex color, return it
  if (typeof color === "string" && color.startsWith("#")) {
    return color;
  }

  // If it's RGB values (space-separated), convert to hex
  if (typeof color === "string" && color.includes(" ")) {
    const [r, g, b] = color.split(" ").map(Number);
    return rgbToHex(r, g, b);
  }

  return String(color);
}

// Refresh all colors from CSS
function refreshColorsFromCSS() {
  const newColors: Record<string, string> = {};
  for (const config of allColorConfigs) {
    newColors[config.vuetifyKey] = readColorFromCSS(config.vuetifyKey);
  }
  currentColors.value = newColors;
}

// Get current color - returns from cache
function getCurrentColor(vuetifyKey: string): string {
  return currentColors.value[vuetifyKey] || "#000000";
}

// Convert RGB to hex
function rgbToHex(r: number, g: number, b: number): string {
  return "#" + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }).join("").toUpperCase();
}

// Update color in Vuetify theme
function updateColor(vuetifyKey: string, hexColor: string) {
  // Get the currently active theme (what's being displayed)
  const activeThemeName = theme.global.name.value;

  // Update the color in the Vuetify theme object (for export and internal state)
  const activeTheme = theme.themes.value[activeThemeName];
  activeTheme.colors[vuetifyKey] = hexColor;

  // Store the override
  colorOverrides.value[vuetifyKey] = hexColor;

  // Update the cached color value immediately
  currentColors.value[vuetifyKey] = hexColor;

  // Debounce the style update to avoid too many DOM operations during rapid color changes
  if (refreshTimer) {
    clearTimeout(refreshTimer);
  }
  refreshTimer = setTimeout(() => {
    // Apply all color overrides to the stylesheet
    applyColorOverrides();

    // Force style recalculation on affected elements
    forceStyleRecalc(vuetifyKey);

    colorUpdateKey.value++;
  }, 50);
}

// Generate export text with all theme values
const exportText = computed(() => {
  const lines: string[] = [];
  lines.push('"key"\t"value"');

  // Add non-color settings first
  lines.push('"ui-site-type"\t"music"');
  lines.push('"ui-music-player-show-playback-speed"\t"false"');
  lines.push('"ui-mode"\t"dark"');
  lines.push('"ui-wsom-contest"\t"true"');
  lines.push('"ui-playlist-label"\t"Radio Stations"');
  lines.push('"ui-website"\t""');

  // Add placeholder logo URLs
  lines.push('"ui-site-logo-192"\t"https://m.epongo.com/site-logos/ePongo/ui-site-logo-192.png?v=2"');
  lines.push('"ui-site-logo-512"\t"https://m.epongo.com/site-logos/ePongo/ui-site-logo-512.png?v=2"');
  lines.push('"ui-site-logo-180"\t"https://m.epongo.com/site-logos/ePongo/ui-site-logo-180.png?v=2"');
  lines.push('"ui-site-logo-maskable-192"\t"https://m.epongo.com/site-logos/ePongo/ui-site-logo-maskable-192.png?v=2"');
  lines.push('"ui-site-logo-maskable-512"\t"https://m.epongo.com/site-logos/ePongo/ui-site-logo-maskable-512.png?v=2"');
  lines.push('"ui-site-logo"\t"https://m.epongo.com/site-logos/ePongo/logo.png?v=2"');
  lines.push('"ui-site-logo-url"\t"https://m.epongo.com/site-logos/ePongo/ui-site-logo-192.png"');

  // Add all color values
  for (const config of allColorConfigs) {
    // Get dark theme color
    const darkColor = theme.themes.value.dark.colors[config.vuetifyKey];
    const darkHex = normalizeColor(darkColor);
    lines.push(`"${config.exportKeyDark}"\t"${darkHex}"`);

    // Get light theme color
    const lightColor = theme.themes.value.light.colors[config.vuetifyKey];
    const lightHex = normalizeColor(lightColor);
    lines.push(`"${config.exportKeyLight}"\t"${lightHex}"`);
  }

  // Add gradient colors (same as primary/secondary)
  const darkPrimary = normalizeColor(theme.themes.value.dark.colors.primary);
  const darkSecondary = normalizeColor(theme.themes.value.dark.colors.secondary);
  const lightPrimary = normalizeColor(theme.themes.value.light.colors.primary);
  const lightSecondary = normalizeColor(theme.themes.value.light.colors.secondary);

  lines.push(`"ui-gradient-start-dark"\t"${darkPrimary}"`);
  lines.push(`"ui-gradient-end-dark"\t"${darkSecondary}"`);
  lines.push(`"ui-gradient-start-light"\t"${lightPrimary}"`);
  lines.push(`"ui-gradient-end-light"\t"${lightSecondary}"`);

  // Add additional UI colors
  lines.push('"ui-error-color"\t"#DC2626"');
  lines.push('"ui-error-color-light"\t"#FEE2E2"');
  lines.push('"ui-on-error"\t"#FFFFFF"');
  lines.push('"ui-warning-color"\t"#F59E0B"');
  lines.push('"ui-warning-color-light"\t"#FEF3C7"');
  lines.push('"ui-on-warning"\t"#000000"');
  lines.push('"ui-info-color"\t"#0EA5E9"');
  lines.push('"ui-info-color-light"\t"#E0F2FE"');
  lines.push('"ui-on-info"\t"#FFFFFF"');
  lines.push('"ui-success-color"\t"#10B981"');
  lines.push('"ui-success-color-light"\t"#D1FAE5"');
  lines.push('"ui-on-success"\t"#FFFFFF"');

  // Additional utility colors
  lines.push('"ui-scrim-color"\t"#000000"');
  lines.push('"ui-backdrop-light"\t"#FFFFFF"');
  lines.push('"ui-backdrop-dark"\t"#000000"');
  lines.push('"ui-focus-outline-light"\t"#3B82F6"');
  lines.push('"ui-focus-outline-dark"\t"#60A5FA"');
  lines.push('"ui-hover-overlay-light"\t"#000000"');
  lines.push('"ui-hover-overlay-dark"\t"#FFFFFF"');
  lines.push('"ui-pressed-overlay-light"\t"#000000"');
  lines.push('"ui-pressed-overlay-dark"\t"#FFFFFF"');
  lines.push('"ui-selection-light"\t"#DBEAFE"');
  lines.push('"ui-selection-dark"\t"#1E3A8A"');
  lines.push('"ui-disabled-light"\t"#9CA3AF"');
  lines.push('"ui-disabled-dark"\t"#6B7280"');
  lines.push('"ui-disabled-background-light"\t"#F3F4F6"');
  lines.push('"ui-disabled-background-dark"\t"#374151"');
  lines.push('"ui-shadow-light"\t"#000000"');
  lines.push('"ui-shadow-dark"\t"#000000"');

  return lines.join('\n');
});

// Normalize color to hex format
function normalizeColor(color: string | undefined): string {
  if (!color) return "#000000";

  // If it's already hex, return it
  if (color.startsWith("#")) {
    return color.toUpperCase();
  }

  // If it's RGB values (space-separated), convert to hex
  if (color.includes(" ")) {
    const [r, g, b] = color.split(" ").map(Number);
    return rgbToHex(r, g, b);
  }

  return color;
}

// Copy export text to clipboard
async function copyToClipboard() {
  try {
    await navigator.clipboard.writeText(exportText.value);
    showCopySnackbar.value = true;
    showExportDialog.value = false;
  } catch (err) {
    console.error("Failed to copy to clipboard:", err);
  }
}

// Reset colors to defaults
function resetToDefaults() {
  // Reset dark theme
  const darkColors = theme.themes.value.dark.colors;
  darkColors.primary = "#F59E0B";
  darkColors["on-primary"] = "#0C0A09";
  darkColors["primary-accent"] = "#7B3FFF";
  darkColors.secondary = "#A8A29E";
  darkColors["on-secondary"] = "#0C0A09";
  darkColors["secondary-accent"] = "#fbbf24";
  darkColors.tertiary = "#34D399";
  darkColors["on-tertiary"] = "#0C0A09";
  darkColors["tertiary-accent"] = "#10B981";
  darkColors.background = "#0C0A09";
  darkColors["on-background"] = "#FAFAF9";
  darkColors.surface = "#1C1917";
  darkColors["surface-variant"] = "#292524";
  darkColors["on-surface"] = "#FAFAF9";
  darkColors["inverse-surface"] = "#FAFAF9";
  darkColors.outline = "#78716C";
  darkColors["outline-variant"] = "#44403C";
  darkColors.error = "#F87171";
  darkColors.warning = "#FBBF24";
  darkColors.info = "#38BDF8";
  darkColors.success = "#4ADE80";

  // Reset light theme
  const lightColors = theme.themes.value.light.colors;
  lightColors.primary = "#D97706";
  lightColors["on-primary"] = "#FFFFFF";
  lightColors["primary-accent"] = "#4A22B3";
  lightColors.secondary = "#78716C";
  lightColors["on-secondary"] = "#FFFFFF";
  lightColors["secondary-accent"] = "#b45309";
  lightColors.tertiary = "#059669";
  lightColors["on-tertiary"] = "#FFFFFF";
  lightColors["tertiary-accent"] = "#047857";
  lightColors.background = "#FAFAF9";
  lightColors["on-background"] = "#1C1917";
  lightColors.surface = "#FFFFFF";
  lightColors["surface-variant"] = "#F5F5F4";
  lightColors["on-surface"] = "#1C1917";
  lightColors["inverse-surface"] = "#1C1917";
  lightColors.outline = "#78716C";
  lightColors["outline-variant"] = "#D6D3D1";
  lightColors.error = "#DC2626";
  lightColors.warning = "#F59E0B";
  lightColors.info = "#0EA5E9";
  lightColors.success = "#10B981";

  // Clear color overrides
  const oldOverrides = { ...colorOverrides.value };
  colorOverrides.value = {};

  // Remove the override stylesheet
  const overrideStyle = document.getElementById("theme-color-picker-overrides");
  if (overrideStyle) {
    overrideStyle.remove();
  }

  // Remove inline CSS variable overrides from theme element
  const currentThemeName = theme.global.name.value;
  const themeElement = document.querySelector(`.v-theme--${currentThemeName}`) as HTMLElement;
  if (themeElement) {
    for (const vuetifyKey of Object.keys(oldOverrides)) {
      themeElement.style.removeProperty(`--v-theme-${vuetifyKey}`);
    }
  }

  // Force theme refresh after reset by toggling theme
  const otherTheme = currentThemeName === "dark" ? "light" : "dark";
  // @ts-ignore - Vuetify's theme.change() is not in types but works at runtime
  theme.change?.(otherTheme);
  nextTick().then(() => {
    // @ts-ignore - Vuetify's theme.change() is not in types but works at runtime
    theme.change?.(currentThemeName);
    colorUpdateKey.value++;
  });
}

// Don't watch for theme changes - we only need to read colors from
// the actual displayed theme element, which has tenant colors applied

// Force a refresh after mount to pick up actual CSS values from tenant settings
onMounted(() => {
  // Initial refresh
  refreshColorsFromCSS();

  // Also refresh after a delay to catch late-applied tenant settings
  setTimeout(() => {
    refreshColorsFromCSS();
  }, 500);

  // And another longer delay just in case
  setTimeout(() => {
    refreshColorsFromCSS();
  }, 1500);
});
</script>

<style scoped lang="scss">
.theme-color-picker {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;

  &__widget {
    background: rgba(255, 255, 255, 0.02);
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 16px;
    overflow: hidden;
  }

  &__widget-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  }

  &__widget-icon {
    color: rgb(var(--v-theme-primary));
  }

  &__widget-title {
    font-family: var(--font-display);
    font-size: 0.9375rem;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    margin: 0;
  }

  &__content {
    padding: 12px;
    max-height: 500px;
    overflow-y: auto;
  }

  &__categories {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__category {
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.04);

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }

  &__category-title {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: rgb(var(--v-theme-outline));
    margin: 0 0 8px 0;
  }

  &__colors {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__export {
    padding: 12px;
  }
}

.export-textarea {
  font-family: monospace;
  font-size: 0.75rem;
}

// Light theme
.v-theme--light {
  .theme-color-picker {
    &__widget {
      background: rgba(0, 0, 0, 0.02);
      border-color: rgba(0, 0, 0, 0.06);
    }

    &__widget-header {
      border-bottom-color: rgba(0, 0, 0, 0.06);
    }

    &__category {
      border-bottom-color: rgba(0, 0, 0, 0.04);
    }
  }
}
</style>
